<template>
    <li class="book-nav__item" :class="{ 'is-active': computedIsActive }">
        <a
            href=""
            class="book-nav__link"
            :class="getLinkClass(node.type_text)"
            :title="node.raw_text"
            @click.prevent="onNavClick(node)"
        >
            <span class="book-nav__chapter-dot" aria-hidden="true" />
            <span class="book-nav__chapter-title">
                {{ reviewerRail ? node.raw_text : trimHeading(node.raw_text) }}
            </span>
            <span v-if="reviewerDuration" class="book-nav__chapter-duration">
                {{ reviewerDuration }}
            </span>
        </a>
        <div
            v-if="sectionInfo"
            v-tooltip="{
                content: sectionInfoText,
                disabled: !sectionInfo,
                placement: 'top',
                container: false
            }"
            class="book-nav__section-info"
        >
            <div class="book-nav__section-badge" :class="sectionInfoClass"></div>
        </div>
        <ul v-if="node.children && node.children.length" class="book-nav__list">
            <EditorNavigationItem
                v-for="child in node.children"
                :key="child.id"
                :node="child"
                :is-active="isActive"
                :active-id="activeId"
                :reviewer-rail="reviewerRail"
                @click="onNavClick"
            />
        </ul>
    </li>
</template>

<script>
import { formatDuration } from '@speechki-frontend/shared/helpers/functions';

const formatReviewerDuration = (duration) => {
    const totalSeconds = Math.floor(Number(duration));
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';

    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);
    const paddedSeconds = String(seconds).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');

    return hours ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`;
};

export default {
    name: 'EditorNavigationItem',
    emits: ['click'],
    props: {
        node: {
            type: Object,
            required: true
        },
        // Only meaningful when reviewerRail is true: highlights the exact
        // node matching this id, without children inheriting a parent's
        // active state.
        activeId: {
            type: [String, Number],
            default: null
        },
        // Master (non-reviewerRail) behavior: a single boolean resolved once
        // per top-level node in EditorNavigation.vue and inherited unchanged
        // by every descendant, so a whole active branch highlights together.
        isActive: {
            type: Boolean,
            default: false
        },
        reviewerRail: {
            type: Boolean,
            default: false
        },
        sectionInfo: {
            type: Object,
            default: null
        }
    },
    textThreshold: 20,
    computed: {
        computedIsActive() {
            return this.reviewerRail ? this.node.id === this.activeId : this.isActive;
        },
        reviewerDuration() {
            if (!this.reviewerRail) return '';
            return formatReviewerDuration(this.node.audio_duration);
        },
        sectionInfoText() {
            if (!this.sectionInfo) return '';

            if (!this.sectionInfo.is_ok) {
                return this.$t('editor.contents.sectionDuration.notOk', {
                    size: (this.sectionInfo.size / 1024).toFixed(2),
                    duration: formatDuration(this.sectionInfo.duration, this.$i18n.locale)
                });
            }

            return this.$t('editor.contents.sectionDuration.ok', {
                size: (this.sectionInfo.size / 1024).toFixed(2),
                duration: formatDuration(this.sectionInfo.duration, this.$i18n.locale)
            });
        },
        sectionInfoClass() {
            if (!this.sectionInfo) return '';

            if (this.sectionInfo.is_ok) {
                return 'book-nav__section-badge--ok';
            } else {
                return 'book-nav__section-badge--not-ok';
            }
        }
    },
    methods: {
        getLinkClass(type) {
            switch (type) {
                case 'title':
                    return 'book-nav__link--title';

                default:
                    break;
            }
        },
        trimHeading(text = '') {
            if (text.length <= this.$options.textThreshold) return text;
            return text.slice(0, this.$options.textThreshold) + '...';
        },
        onNavClick(node) {
            this.$emit('click', node);
        }
    }
};
</script>
